import { create } from 'zustand';
import type { Database } from '../types/database.types';

type Machine = Database['public']['Tables']['machines']['Row'];
type RentalPackage = Database['public']['Tables']['rental_packages']['Row'];
type AddOn = Database['public']['Tables']['add_ons']['Row'];
type DeliveryZone = Database['public']['Tables']['delivery_zones']['Row'];

interface BookingData {
  machine: Machine | null;
  package: RentalPackage | null;
  startDate: string | null;
  startTime: string | null;
  endDate: string | null;
  endTime: string | null;
  deliveryType: 'delivery' | 'pickup' | null;
  deliveryAddressId: string | null;
  deliveryAddress: string | null;
  deliveryZone: DeliveryZone | null;
  pickupLocationId: string | null;
  addOns: Array<{ addOn: AddOn; quantity: number }>;
  specialInstructions: string | null;
  promoCode: string | null;
  promoDiscount: number;
  subtotal: number;
  deliveryFee: number;
  addOnsTotal: number;
  total: number;
  depositAmount: number;
  waiveDeposit: boolean;
}

interface BookingState {
  bookingData: BookingData;
  currentStep: number;
  totalSteps: number;

  // Actions
  setMachine: (machine: Machine) => void;
  setPackage: (pkg: RentalPackage) => void;
  setDateTime: (startDate: string, startTime: string, endDate: string, endTime: string) => void;
  setDeliveryType: (type: 'delivery' | 'pickup') => void;
  setDeliveryAddress: (addressId: string, address: string, zone: DeliveryZone | null) => void;
  setPickupLocation: (locationId: string) => void;
  addAddOn: (addOn: AddOn, quantity: number) => void;
  removeAddOn: (addOnId: string) => void;
  updateAddOnQuantity: (addOnId: string, quantity: number) => void;
  setSpecialInstructions: (instructions: string) => void;
  applyPromoCode: (code: string, discount: number) => void;
  removePromoCode: () => void;
  setWaiveDeposit: (waive: boolean) => void;
  calculateTotals: () => void;
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  resetBooking: () => void;
}

const initialBookingData: BookingData = {
  machine: null,
  package: null,
  startDate: null,
  startTime: null,
  endDate: null,
  endTime: null,
  deliveryType: null,
  deliveryAddressId: null,
  deliveryAddress: null,
  deliveryZone: null,
  pickupLocationId: null,
  addOns: [],
  specialInstructions: null,
  promoCode: null,
  promoDiscount: 0,
  subtotal: 0,
  deliveryFee: 0,
  addOnsTotal: 0,
  total: 0,
  depositAmount: 0,
  waiveDeposit: false,
};

export const useBookingStore = create<BookingState>((set, get) => ({
  bookingData: initialBookingData,
  currentStep: 1,
  totalSteps: 8,

  setMachine: (machine) => {
    set((state) => ({
      bookingData: { ...state.bookingData, machine },
    }));
    get().calculateTotals();
  },

  setPackage: (pkg) => {
    set((state) => ({
      bookingData: {
        ...state.bookingData,
        package: pkg,
        subtotal: pkg.price,
        depositAmount: pkg.deposit_required,
      },
    }));
    get().calculateTotals();
  },

  setDateTime: (startDate, startTime, endDate, endTime) => {
    set((state) => ({
      bookingData: {
        ...state.bookingData,
        startDate,
        startTime,
        endDate,
        endTime,
      },
    }));
  },

  setDeliveryType: (type) => {
    set((state) => ({
      bookingData: {
        ...state.bookingData,
        deliveryType: type,
        deliveryFee: type === 'pickup' ? 0 : state.bookingData.deliveryFee,
      },
    }));
    get().calculateTotals();
  },

  setDeliveryAddress: (addressId, address, zone) => {
    set((state) => ({
      bookingData: {
        ...state.bookingData,
        deliveryAddressId: addressId,
        deliveryAddress: address,
        deliveryZone: zone,
        deliveryFee: zone?.delivery_fee || 0,
      },
    }));
    get().calculateTotals();
  },

  setPickupLocation: (locationId) => {
    set((state) => ({
      bookingData: {
        ...state.bookingData,
        pickupLocationId: locationId,
        deliveryFee: 0,
      },
    }));
    get().calculateTotals();
  },

  addAddOn: (addOn, quantity) => {
    set((state) => {
      const existingAddOnIndex = state.bookingData.addOns.findIndex(
        (item) => item.addOn.id === addOn.id
      );

      let newAddOns;
      if (existingAddOnIndex >= 0) {
        newAddOns = [...state.bookingData.addOns];
        newAddOns[existingAddOnIndex] = { addOn, quantity };
      } else {
        newAddOns = [...state.bookingData.addOns, { addOn, quantity }];
      }

      return {
        bookingData: { ...state.bookingData, addOns: newAddOns },
      };
    });
    get().calculateTotals();
  },

  removeAddOn: (addOnId) => {
    set((state) => ({
      bookingData: {
        ...state.bookingData,
        addOns: state.bookingData.addOns.filter((item) => item.addOn.id !== addOnId),
      },
    }));
    get().calculateTotals();
  },

  updateAddOnQuantity: (addOnId, quantity) => {
    set((state) => ({
      bookingData: {
        ...state.bookingData,
        addOns: state.bookingData.addOns.map((item) =>
          item.addOn.id === addOnId ? { ...item, quantity } : item
        ),
      },
    }));
    get().calculateTotals();
  },

  setSpecialInstructions: (instructions) => {
    set((state) => ({
      bookingData: { ...state.bookingData, specialInstructions: instructions },
    }));
  },

  applyPromoCode: (code, discount) => {
    set((state) => ({
      bookingData: {
        ...state.bookingData,
        promoCode: code,
        promoDiscount: discount,
      },
    }));
    get().calculateTotals();
  },

  removePromoCode: () => {
    set((state) => ({
      bookingData: {
        ...state.bookingData,
        promoCode: null,
        promoDiscount: 0,
      },
    }));
    get().calculateTotals();
  },

  setWaiveDeposit: (waive) => {
    set((state) => ({
      bookingData: { ...state.bookingData, waiveDeposit: waive },
    }));
    get().calculateTotals();
  },

  calculateTotals: () => {
    set((state) => {
      const { subtotal, deliveryFee, addOns, promoDiscount, package: pkg } = state.bookingData;

      const addOnsTotal = addOns.reduce(
        (sum, item) => sum + item.addOn.price * item.quantity,
        0
      );

      const totalBeforePromo = subtotal + deliveryFee + addOnsTotal;
      const total = Math.max(0, totalBeforePromo - promoDiscount);

      // Deposit is waived when the user verified their identity, OR when
      // the Machine Operator add-on is selected.
      const hasMachineOperator = addOns.some(
        (item) => item.addOn.name === 'Machine Operator'
      );
      const waived = state.bookingData.waiveDeposit || hasMachineOperator;
      const depositAmount = waived ? 0 : (pkg?.deposit_required || 0);

      return {
        bookingData: {
          ...state.bookingData,
          addOnsTotal,
          total,
          depositAmount,
        },
      };
    });
  },

  setCurrentStep: (step) => set({ currentStep: step }),

  nextStep: () =>
    set((state) => ({
      currentStep: Math.min(state.currentStep + 1, state.totalSteps),
    })),

  prevStep: () =>
    set((state) => ({
      currentStep: Math.max(state.currentStep - 1, 1),
    })),

  resetBooking: () =>
    set({
      bookingData: initialBookingData,
      currentStep: 1,
    }),
}));
